// src/components/MetodoPagamentoPublico.jsx

import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Paper,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
  Alert,
  AlertTitle,
  Button,
  Divider,
  styled
} from '@mui/material';
import { formatarMoeda } from '../../utils/formatters';

import { styled as muiStyled } from '@mui/material/styles';

const MetodoLogo = muiStyled('img')({
  height: 40,
  maxWidth: 120,
  objectFit: 'contain',
  margin: '10px 0'
});

const MetodoPagamentoPublico = ({
  valorTotal,
  valorOriginal,
  valorMulta,
  metodosDisponiveis,
  onMetodoPagamentoChange,
  error
}) => {
  const [metodoPagamento, setMetodoPagamento] = useState('');
  const [dadosPagamento, setDadosPagamento] = useState({
    numeroTelefone: '',
    entidade: '',
    referencia: '',
    numero: '',
    validade: '',
    cvv: ''
  });

  const [validacaoLocal, setValidacaoLocal] = useState({
    numeroTelefone: '',
    entidade: '',
    referencia: '',
    numero: '',
    validade: '',
    cvv: ''
  });

  const validarNumeroTelefone = (numero) => {
    if (!numero) return false;
    const numeroLimpo = numero.replace(/\D/g, '');
    return /^(84|85|86|87)[0-9]{7}$/.test(numeroLimpo);
  };

  const validarEntidade = (entidade) => {
    return /^\d{5}$/.test(entidade);
  };

  const validarReferencia = (referencia) => {
    return /^\d{9}$/.test(referencia);
  };

  const validarCartao = (numero, validade, cvv) => {
    const numeroValido = /^\d{16}$/.test(numero.replace(/\s/g, ''));
    const validadeValida = /^(0[1-9]|1[0-2])\/([0-9]{2})$/.test(validade);
    const cvvValido = /^\d{3,4}$/.test(cvv);
    
    if (!numeroValido) return 'Número de cartão inválido';
    if (!validadeValida) return 'Data de validade inválida (MM/AA)';
    if (!cvvValido) return 'CVV inválido';
    return '';
  };

  const handleDadosChange = (campo, valor) => {
    let valorFormatado = valor;

    switch (campo) {
      case 'numeroTelefone':
      case 'entidade':
      case 'referencia':
      case 'cvv':
        valorFormatado = valor.replace(/\D/g, '');
        break;
      
      case 'numero':
        valorFormatado = valor.replace(/\D/g, '')
          .replace(/(\d{4})/g, '$1 ')
          .trim();
        break;
        
      case 'validade':
        valorFormatado = valor
          .replace(/\D/g, '')
          .replace(/(\d{2})(\d)/, '$1/$2')
          .substring(0, 5);
        break;
      default:
        break;
    }

    const novosDados = { ...dadosPagamento, [campo]: valorFormatado };
    setDadosPagamento(novosDados);

    let dadosPagamentoFormatado = {};
    let dadosValidos = false;
    let mensagemErro = '';

    switch (metodoPagamento) {
      case 'MPESA':
      case 'EMOLA': {
        const numeroValido = validarNumeroTelefone(novosDados.numeroTelefone);
        dadosValidos = numeroValido;
        mensagemErro = numeroValido ? '' : 'Número de telefone inválido';
        dadosPagamentoFormatado = {
          numeroTelefone: novosDados.numeroTelefone
        };
        break;
      }
      case 'BCI':
      case 'STANDARDBANK':
      case 'MOZABANK': {
        const entidadeValida = validarEntidade(novosDados.entidade);
        const referenciaValida = validarReferencia(novosDados.referencia);
        dadosValidos = entidadeValida && referenciaValida;
        mensagemErro = !dadosValidos ? 'Dados bancários inválidos' : '';
        dadosPagamentoFormatado = {
          entidade: novosDados.entidade,
          referencia: novosDados.referencia
        };
        break;
      }
      case 'CARTAO': {
        const erroCartao = validarCartao(
          novosDados.numero,
          novosDados.validade,
          novosDados.cvv
        );
        dadosValidos = !erroCartao;
        mensagemErro = erroCartao;
        dadosPagamentoFormatado = {
          numero: novosDados.numero,
          validade: novosDados.validade,
          cvv: novosDados.cvv
        };
        break;
      }
      default:
        break;
    }

    setValidacaoLocal(prev => ({
      ...prev,
      [campo]: mensagemErro
    }));

    if (dadosValidos) {
      onMetodoPagamentoChange({
        metodoPagamento,
        dadosPagamento: dadosPagamentoFormatado
      });
    }
  };

  const renderCamposMetodo = () => {
    switch (metodoPagamento) {
      case 'MPESA':
      case 'EMOLA':
        return (
          <TextField
            fullWidth
            label={`Número ${metodoPagamento}`}
            value={dadosPagamento.numeroTelefone}
            onChange={(e) => handleDadosChange('numeroTelefone', e.target.value)}
            error={!!validacaoLocal.numeroTelefone}
            helperText={validacaoLocal.numeroTelefone || 'Formato: 84XXXXXXX'}
            InputProps={{
              startAdornment: <InputAdornment position="start">+258</InputAdornment>
            }}
            sx={{ mb: 2 }}
          />
        );

      case 'BCI':
      case 'STANDARDBANK':
      case 'MOZABANK':
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              label="Entidade"
              value={dadosPagamento.entidade}
              onChange={(e) => handleDadosChange('entidade', e.target.value)}
              error={!!validacaoLocal.entidade}
              helperText={validacaoLocal.entidade || '5 dígitos'}
            />
            <TextField
              fullWidth
              label="Referência"
              value={dadosPagamento.referencia}
              onChange={(e) => handleDadosChange('referencia', e.target.value)}
              error={!!validacaoLocal.referencia}
              helperText={validacaoLocal.referencia || '9 dígitos'}
            />
          </Box>
        );

      case 'CARTAO':
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              label="Número do Cartão"
              value={dadosPagamento.numero}
              onChange={(e) => handleDadosChange('numero', e.target.value)}
              error={!!validacaoLocal.numero}
              helperText={validacaoLocal.numero || '16 dígitos'}
              sx={{ mb: 2 }}
            />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Validade"
                value={dadosPagamento.validade}
                onChange={(e) => handleDadosChange('validade', e.target.value)}
                error={!!validacaoLocal.validade}
                helperText={validacaoLocal.validade || 'MM/AA'}
                placeholder="MM/AA"
                sx={{ width: '50%' }}
              />
              <TextField
                label="CVV"
                type="password"
                value={dadosPagamento.cvv}
                onChange={(e) => handleDadosChange('cvv', e.target.value)}
                error={!!validacaoLocal.cvv}
                helperText={validacaoLocal.cvv || '3-4 dígitos'}
                sx={{ width: '50%' }}
              />
            </Box>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Paper elevation={0} sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        Método de Pagamento
      </Typography>

      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle1" gutterBottom>
          Valor a Pagar: {formatarMoeda(valorTotal)}
        </Typography>
        {valorMulta > 0 && (
          <Alert severity="warning" sx={{ mt: 1 }}>
            <AlertTitle>Pagamento com Multa</AlertTitle>
            <Typography>Valor original: {formatarMoeda(valorOriginal)}</Typography>
            <Typography>Multa: {formatarMoeda(valorMulta)}</Typography>
          </Alert>
        )}
      </Box>

      <Divider sx={{ my: 3 }} />

      <FormControl fullWidth sx={{ mb: 3 }}>
        <InputLabel>Selecione o Método de Pagamento</InputLabel>
        <Select
          value={metodoPagamento}
          onChange={(e) => {
            setMetodoPagamento(e.target.value);
            setDadosPagamento({
              numeroTelefone: '',
              entidade: '',
              referencia: '',
              numero: '',
              validade: '',
              cvv: ''
            });
            setValidacaoLocal({
              numeroTelefone: '',
              entidade: '',
              referencia: '',
              numero: '',
              validade: '',
              cvv: ''
            });
          }}
          label="Selecione o Método de Pagamento"
        >
          {Object.entries(metodosDisponiveis).map(([key, metodo]) => (
            <MenuItem key={key} value={key}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                {metodo.logo && <MetodoLogo src={metodo.logo} alt={metodo.nome} />}
                <Typography>{metodo.nome}</Typography>
              </Box>
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {metodoPagamento && metodosDisponiveis[metodoPagamento] && (
        <Alert severity="info" sx={{ mb: 3 }}>
          {metodosDisponiveis[metodoPagamento].instrucoes}
        </Alert>
      )}

      {metodoPagamento && (
        <Box sx={{ mt: 2 }}>
          {renderCamposMetodo()}
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}
    </Paper>
  );
};

MetodoPagamentoPublico.propTypes = {
  valorTotal: PropTypes.number.isRequired,
  valorOriginal: PropTypes.number.isRequired,
  valorMulta: PropTypes.number,
  metodosDisponiveis: PropTypes.object.isRequired,
  onMetodoPagamentoChange: PropTypes.func.isRequired,
  error: PropTypes.string
};

MetodoPagamentoPublico.defaultProps = {
  valorMulta: 0,
  error: ''
};

export default MetodoPagamentoPublico;
